// src/pages/Trips.js
import React, { useEffect, useState, useRef } from "react";
import {
  db,
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  serverTimestamp,
} from "../firebase";
import { v4 as uuidv4 } from "uuid";
import LoadingSpinner from "../components/LoadingSpinner";
import CollapsibleSection from "../components/CollapsibleSection";
import ConfirmModal from "../components/ConfirmModal";

const FeedbackModal = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => onClose(), 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: "400px", padding: "20px" }}>
        <h3>{type === "success" ? "Success" : "Error"}</h3>
        <p style={{ color: type === "success" ? "#15803d" : "#b91c1c" }}>
          {message}
        </p>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Add Cost Modal Component
const AddCostModal = ({ trip, onClose, onAddCost }) => {
  const [costType, setCostType] = useState("fuel");
  const [formData, setFormData] = useState({
    date: new Date().toISOString().slice(0, 10),
    odometer: "",
    liters: "",
    cost_per_liter: "",
    component: "",
    description: "",
    amount: "",
    fine_type: "",
  });
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onAddCost(costType, formData);
    onClose();
  };

  const renderFuelForm = () => (
    <div className="cost-form-grid">
      <div className="form-cell">
        <label>Date</label>
        <input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required />
      </div>
      <div className="form-cell">
        <label>Odometer (km)</label>
        <input type="number" value={formData.odometer} onChange={(e) => setFormData({ ...formData, odometer: e.target.value })} required />
      </div>
      <div className="form-cell">
        <label>Liters</label>
        <input type="number" step="0.1" value={formData.liters} onChange={(e) => setFormData({ ...formData, liters: e.target.value })} required />
      </div>
      <div className="form-cell">
        <label>Cost per Liter ({trip.currency_dispatch})</label>
        <input type="number" step="0.01" value={formData.cost_per_liter} onChange={(e) => setFormData({ ...formData, cost_per_liter: e.target.value })} required />
      </div>
      <div className="form-cell">
        <label>Total Cost ({trip.currency_dispatch})</label>
        <input type="number" value={(parseFloat(formData.liters || 0) * parseFloat(formData.cost_per_liter || 0)).toFixed(2)} readOnly disabled />
      </div>
    </div>
  );

  const renderMechanicalForm = () => (
    <div className="cost-form-grid">
      <div className="form-cell">
        <label>Date</label>
        <input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required />
      </div>
      <div className="form-cell">
        <label>Component</label>
        <select value={formData.component} onChange={(e) => setFormData({ ...formData, component: e.target.value })} required>
          <option value="">Select Component</option>
          <option value="Engine">Engine</option>
          <option value="Suspension">Suspension</option>
          <option value="Transmission">Transmission</option>
          <option value="Trailer">Trailer</option>
          <option value="Brakes">Brakes</option>
          <option value="Tires">Tires</option>
          <option value="Electrical">Electrical</option>
        </select>
      </div>
      <div className="form-cell">
        <label>Description</label>
        <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows="2" required />
      </div>
      <div className="form-cell">
        <label>Cost ({trip.currency_dispatch})</label>
        <input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required />
      </div>
    </div>
  );

  const renderRoadCostForm = () => (
    <div className="cost-form-grid">
      <div className="form-cell">
        <label>Date</label>
        <input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required />
      </div>
      <div className="form-cell">
        <label>Type</label>
        <select value={formData.fine_type} onChange={(e) => setFormData({ ...formData, fine_type: e.target.value })} required>
          <option value="">Select Type</option>
          <option value="Traffic Fine">Traffic Fine</option>
          <option value="Toll">Toll</option>
          <option value="Parking">Parking</option>
          <option value="Other">Other</option>
        </select>
      </div>
      <div className="form-cell">
        <label>Description</label>
        <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows="2" required />
      </div>
      <div className="form-cell">
        <label>Cost ({trip.currency_dispatch})</label>
        <input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required />
      </div>
    </div>
  );

  const renderCustomsForm = () => (
    <div className="cost-form-grid">
      <div className="form-cell">
        <label>Date</label>
        <input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required />
      </div>
      <div className="form-cell">
        <label>Type</label>
        <select value={formData.fine_type} onChange={(e) => setFormData({ ...formData, fine_type: e.target.value })} required>
          <option value="">Select Type</option>
          <option value="Fines">Fines</option>
          <option value="Clearance Fee">Clearance Fee</option>
          <option value="Import Duty">Import Duty</option>
          <option value="Other">Other</option>
        </select>
      </div>
      <div className="form-cell">
        <label>Description</label>
        <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows="2" required />
      </div>
      <div className="form-cell">
        <label>Cost ({trip.currency_dispatch})</label>
        <input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required />
      </div>
    </div>
  );

  return (
    <div className="modal-overlay">
      <div className="modal add-cost-modal" ref={modalRef} style={{ maxWidth: "650px" }}>
        <h3>➕ Add Cost to Trip: {trip.trip_code}</h3>
        <div className="cost-type-tabs">
          <button type="button" className={`cost-tab ${costType === "fuel" ? "active" : ""}`} onClick={() => setCostType("fuel")}>⛽ Fuel</button>
          <button type="button" className={`cost-tab ${costType === "mechanical" ? "active" : ""}`} onClick={() => setCostType("mechanical")}>🔧 Mechanical</button>
          <button type="button" className={`cost-tab ${costType === "road" ? "active" : ""}`} onClick={() => setCostType("road")}>🚓 Road Cost</button>
          <button type="button" className={`cost-tab ${costType === "customs" ? "active" : ""}`} onClick={() => setCostType("customs")}>🛃 Customs</button>
        </div>
        <form onSubmit={handleSubmit}>
          {costType === "fuel" && renderFuelForm()}
          {costType === "mechanical" && renderMechanicalForm()}
          {costType === "road" && renderRoadCostForm()}
          {costType === "customs" && renderCustomsForm()}
          <div className="modal-actions">
            <button type="submit" className="btn-primary">Add Cost</button>
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Update Trip Status Modal
const UpdateStatusModal = ({ trip, onClose, onUpdateStatus }) => {
  const [statusAction, setStatusAction] = useState("complete");
  const [formData, setFormData] = useState({
    final_odometer: "",
    pods_submitted: "no",
    delay_reason: "",
    delay_description: "",
    not_completed_reason: "",
    not_completed_notes: "",
  });
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onUpdateStatus(statusAction, formData);
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal update-status-modal" ref={modalRef} style={{ maxWidth: "550px" }}>
        <h3>📋 Update Trip Status: {trip.trip_code}</h3>
        <div className="status-action-tabs">
          <button type="button" className={`status-tab ${statusAction === "complete" ? "active" : ""}`} onClick={() => setStatusAction("complete")}>✅ Complete Trip</button>
          <button type="button" className={`status-tab ${statusAction === "delayed" ? "active" : ""}`} onClick={() => setStatusAction("delayed")}>⏰ Mark as Delayed</button>
          <button type="button" className={`status-tab ${statusAction === "not_completed" ? "active" : ""}`} onClick={() => setStatusAction("not_completed")}>🔄 Not Completed</button>
        </div>
        <form onSubmit={handleSubmit}>
          {statusAction === "complete" && (
            <div className="status-form">
              <div className="form-cell">
                <label>Final Odometer Reading (km)</label>
                <input type="number" value={formData.final_odometer} onChange={(e) => setFormData({ ...formData, final_odometer: e.target.value })} required />
              </div>
              <div className="form-cell">
                <label>PODs Submitted?</label>
                <select value={formData.pods_submitted} onChange={(e) => setFormData({ ...formData, pods_submitted: e.target.value })}>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
          )}
          {statusAction === "delayed" && (
            <div className="status-form">
              <div className="form-cell">
                <label>Delay Reason</label>
                <select value={formData.delay_reason} onChange={(e) => setFormData({ ...formData, delay_reason: e.target.value })} required>
                  <option value="">Select Reason</option>
                  <option value="Customs">Customs Clearance</option>
                  <option value="Mechanical">Mechanical Breakdown</option>
                  <option value="Weather">Weather Conditions</option>
                  <option value="Accident">Accident</option>
                  <option value="Border Delay">Border Delay</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-cell">
                <label>Delay Description</label>
                <textarea value={formData.delay_description} onChange={(e) => setFormData({ ...formData, delay_description: e.target.value })} rows="3" required />
              </div>
            </div>
          )}
          {statusAction === "not_completed" && (
            <div className="status-form">
              <div className="form-cell">
                <label>Reason</label>
                <select value={formData.not_completed_reason} onChange={(e) => setFormData({ ...formData, not_completed_reason: e.target.value })} required>
                  <option value="">Select Reason</option>
                  <option value="PODs Not Submitted">PODs Not Submitted</option>
                  <option value="Customer Dispute">Customer Dispute</option>
                  <option value="Documentation Missing">Documentation Missing</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-cell">
                <label>Additional Notes</label>
                <textarea value={formData.not_completed_notes} onChange={(e) => setFormData({ ...formData, not_completed_notes: e.target.value })} rows="3" />
              </div>
            </div>
          )}
          <div className="modal-actions">
            <button type="submit" className="btn-primary">Update Status</button>
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// View Status Details Modal
const ViewStatusDetailsModal = ({ trip, onClose }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return (
    <div className="modal-overlay">
      <div className="modal" ref={modalRef} style={{ maxWidth: "500px" }}>
        <h3>📋 Status Details: {trip.trip_code}</h3>
        <div className="status-details">
          <div className="detail-group">
            <label>Current Status</label>
            <p className={`status-badge status-${trip.status?.toLowerCase()}`}>{trip.status || "draft"}</p>
          </div>
          {trip.status === "Completed" && (
            <>
              <div className="detail-group">
                <label>Final Odometer</label>
                <p>{trip.final_odometer || "N/A"} km</p>
              </div>
              <div className="detail-group">
                <label>PODs Submitted</label>
                <p>{trip.pods_submitted === "yes" ? "✅ Yes" : "❌ No"}</p>
              </div>
              <div className="detail-group">
                <label>Completed Date</label>
                <p>{trip.completed_date ? new Date(trip.completed_date).toLocaleDateString() : "N/A"}</p>
              </div>
            </>
          )}
          {trip.status === "Delayed" && (
            <>
              <div className="detail-group">
                <label>Delay Reason</label>
                <p>{trip.delay_reason || "N/A"}</p>
              </div>
              <div className="detail-group">
                <label>Delay Description</label>
                <p>{trip.delay_description || "N/A"}</p>
              </div>
              <div className="detail-group">
                <label>Delayed Date</label>
                <p>{trip.delayed_date ? new Date(trip.delayed_date).toLocaleDateString() : "N/A"}</p>
              </div>
            </>
          )}
          {trip.status === "Not Completed" && (
            <>
              <div className="detail-group">
                <label>Reason</label>
                <p>{trip.not_completed_reason || "N/A"}</p>
              </div>
              <div className="detail-group">
                <label>Additional Notes</label>
                <p>{trip.not_completed_notes || "N/A"}</p>
              </div>
              <div className="detail-group">
                <label>Date</label>
                <p>{trip.not_completed_date ? new Date(trip.not_completed_date).toLocaleDateString() : "N/A"}</p>
              </div>
            </>
          )}
          {trip.status === "draft" && (
            <div className="detail-group">
              <label>Info</label>
              <p>Trip is still in draft mode. Start the trip to begin tracking.</p>
            </div>
          )}
          {trip.status === "Active" && (
            <div className="detail-group">
              <label>Info</label>
              <p>Trip is currently active. Use "Update Trip Status" to complete, delay, or mark as not completed.</p>
            </div>
          )}
        </div>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

// Updated Action Modal with new options
const ActionModal = ({ trip, onClose, onGenerateDispatch, onStart, onEdit, onDelete, onLogComplaint, onViewTrip, onAddCost, onUpdateStatus }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const isActive = trip.status === "Active";
  const isCompleted = trip.status === "Completed";
  const isDraft = trip.status === "draft";

  return (
    <div className="modal-overlay">
      <div className="modal" ref={modalRef}>
        <h3>Actions for Trip: {trip.trip_code}</h3>
        {isCompleted && (
          <p style={{ color: "#b91c1c" }}>This trip is completed. Limited actions are available.</p>
        )}
        {isActive && (
          <p style={{ color: "#d97706" }}>This trip is active. You can add costs and update status.</p>
        )}
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onViewTrip}>👁️ View Trip</button>
          <button className="btn-secondary" onClick={onGenerateDispatch} disabled={isCompleted}>📄 Generate Dispatch List</button>
          {isDraft && <button className="btn-secondary" onClick={onStart}>🚀 Start Trip</button>}
          {isDraft && <button className="btn-secondary" onClick={onEdit}>✏️ Edit Trip</button>}
          {isActive && <button className="btn-secondary" onClick={onAddCost}>💰 Add Cost</button>}
          {isActive && <button className="btn-secondary" onClick={onUpdateStatus}>📋 Update Trip Status</button>}
          <button className="btn-secondary" onClick={onLogComplaint} disabled={isCompleted}>⚠️ Log Complaint</button>
          {isDraft && <button className="btn-delete" onClick={onDelete}>🗑️ Delete Trip</button>}
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// View Trip Modal
const ViewTripModal = ({ trip, breakdowns, routes, agents, onClose, onAddExtraCost, onAddFuel }) => {
  const [extraCost, setExtraCost] = useState({ description: "", amount: "", currency: "USD" });
  const [fuel, setFuel] = useState({ amount: "", odometer: "", date: new Date().toISOString().slice(0, 10) });
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const route = routes.find((r) => r.id === trip.route_id);
  const routeName = route ? route.name : "Unknown";
  const routePoints = route ? route.points.join(" → ") : "N/A";
  const borderPosts = route?.border_posts || [];
  const associatedAgents = agents.filter((a) => borderPosts.includes(a.id));
  const selectedBreakdowns = breakdowns.filter((b) => trip.breakdowns?.includes(b.id));
  const totalBreakdownCost = selectedBreakdowns.reduce((sum, b) => sum + (b.cost || 0), 0);
  const totalExtraCost = trip.extra_costs?.reduce((sum, ec) => sum + (ec.amount || 0), 0) || 0;
  const fuelUsage = trip.fuel_records?.length > 1
    ? ((trip.fuel_records[trip.fuel_records.length - 1].odometer - trip.fuel_records[0].odometer) / 
       trip.fuel_records.reduce((sum, f) => sum + f.amount, 0)).toFixed(2)
    : "N/A";

  const handleExtraCostSubmit = async (e) => {
    e.preventDefault();
    if (!extraCost.description || !extraCost.amount) return;
    await onAddExtraCost({ description: extraCost.description, amount: Number(extraCost.amount), currency: extraCost.currency });
    setExtraCost({ description: "", amount: "", currency: "USD" });
  };

  const handleFuelSubmit = async (e) => {
    e.preventDefault();
    if (!fuel.amount || !fuel.odometer || !fuel.date) return;
    await onAddFuel({ amount: Number(fuel.amount), odometer: Number(fuel.odometer), date: fuel.date });
    setFuel({ amount: "", odometer: "", date: new Date().toISOString().slice(0, 10) });
  };

  return (
    <div className="modal-overlay">
      <div className="modal view-trip-modal" ref={modalRef} style={{ maxWidth: "900px" }}>
        <h3>📋 Trip Details: {trip.trip_code}</h3>
        
        <div className="view-trip-grid">
          <div className="view-trip-column">
            <div className="detail-group">
              <label>Trip Code</label>
              <p>{trip.trip_code}</p>
            </div>
            <div className="detail-group">
              <label>Booking Reference</label>
              <p>{trip.booking_ref || "N/A"}</p>
            </div>
            <div className="detail-group">
              <label>Truck</label>
              <p>{trip.truck || "N/A"}</p>
            </div>
            <div className="detail-group">
              <label>Driver</label>
              <p>{trip.driver || "N/A"}</p>
            </div>
            <div className="detail-group">
              <label>Trailers</label>
              <p>{trip.trailers?.length > 0 ? trip.trailers.join(", ") : "None"}</p>
            </div>
            <div className="detail-group">
              <label>Start Odometer</label>
              <p>{trip.start_km || "N/A"} km</p>
            </div>
            <div className="detail-group">
              <label>Origin</label>
              <p>{trip.origin || "N/A"}</p>
            </div>
            <div className="detail-group">
              <label>Route</label>
              <p>{routeName}</p>
              <p className="detail-sub">{routePoints}</p>
            </div>
          </div>

          <div className="view-trip-column">
            <div className="detail-group">
              <label>Destination</label>
              <p>{trip.destination || "N/A"}</p>
            </div>
            <div className="detail-group">
              <label>Consignment</label>
              <p>{trip.consignment_description || "N/A"}</p>
            </div>
            <div className="detail-group">
              <label>Tonnage</label>
              <p>{trip.tonnage || "0"} tons</p>
            </div>
            <div className="detail-group">
              <label>Special Instructions</label>
              <p>{trip.special_instructions || "None"}</p>
            </div>
            <div className="detail-group">
              <label>Agents</label>
              <p>{associatedAgents.length > 0 ? associatedAgents.map(a => a.name).join(", ") : "None"}</p>
            </div>
            <div className="detail-group">
              <label>Status</label>
              <p className={`status-badge status-${trip.status?.toLowerCase()}`}>{trip.status || "draft"}</p>
            </div>
            <div className="detail-group">
              <label>Created At</label>
              <p>{new Date(trip.createdAt).toLocaleDateString()}</p>
            </div>
          </div>
        </div>

        <div className="view-trip-section">
          <h4>💰 Financial Details</h4>
          <div className="financial-grid">
            <div className="detail-group">
              <label>Initial Fuel Cost</label>
              <p>{trip.currency_fuel} {trip.initial_fuel_cost?.toLocaleString() || 0}</p>
            </div>
            <div className="detail-group">
              <label>Initial Dispatch Money</label>
              <p>{trip.currency_dispatch} {trip.initial_dispatch_money?.toLocaleString() || 0}</p>
            </div>
            <div className="detail-group">
              <label>Cost Templates</label>
              {selectedBreakdowns.length > 0 ? (
                <ul className="compact-list">
                  {selectedBreakdowns.map((b) => (
                    <li key={b.id}>{b.description}: {b.currency} {b.cost.toLocaleString()}</li>
                  ))}
                </ul>
              ) : (
                <p>No cost templates selected</p>
              )}
            </div>
            <div className="detail-group">
              <label>Total Cost Templates Cost</label>
              <p>{trip.currency_dispatch} {totalBreakdownCost.toLocaleString()}</p>
            </div>
            <div className="detail-group">
              <label>Extra Costs</label>
              {trip.extra_costs?.length > 0 ? (
                <ul className="compact-list">
                  {trip.extra_costs.map((ec, idx) => (
                    <li key={idx}>{ec.description}: {ec.currency} {ec.amount.toLocaleString()}</li>
                  ))}
                </ul>
              ) : (
                <p>No extra costs</p>
              )}
            </div>
            <div className="detail-group">
              <label>Total Extra Cost</label>
              <p>{trip.currency_dispatch} {totalExtraCost.toLocaleString()}</p>
            </div>
            <div className="detail-group">
              <label>Total Cost</label>
              <p className="total-cost">{trip.currency_dispatch} {trip.total_cost.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="view-trip-section">
          <h4>⛽ Fuel Records</h4>
          {trip.fuel_records?.length > 0 ? (
            <div className="fuel-records-list">
              {trip.fuel_records.map((f, idx) => (
                <div key={idx} className="fuel-record-item">
                  <span className="fuel-date">{f.date}</span>
                  <span className="fuel-amount">{f.amount} L</span>
                  <span className="fuel-odometer">at {f.odometer} km</span>
                </div>
              ))}
              <div className="fuel-usage">
                <strong>Fuel Usage:</strong> {fuelUsage} km/L
              </div>
            </div>
          ) : (
            <p>No fuel records</p>
          )}
        </div>

        {trip.status === "Active" && (
          <div className="view-trip-actions-section">
            <div className="add-cost-section">
              <h4>➕ Add Extra Cost</h4>
              <form className="inline-form" onSubmit={handleExtraCostSubmit}>
                <input type="text" value={extraCost.description} onChange={(e) => setExtraCost({ ...extraCost, description: e.target.value })} placeholder="Cost description" />
                <input type="number" value={extraCost.amount} onChange={(e) => setExtraCost({ ...extraCost, amount: e.target.value })} placeholder="Amount" />
                <select value={extraCost.currency} onChange={(e) => setExtraCost({ ...extraCost, currency: e.target.value })}>
                  {["USD", "ZAR", "BWP", "ZMW", "TZS"].map((c) => (<option key={c}>{c}</option>))}
                </select>
                <button type="submit" className="btn-primary">Add Cost</button>
              </form>
            </div>
            <div className="add-fuel-section">
              <h4>⛽ Add Fuel Record</h4>
              <form className="inline-form" onSubmit={handleFuelSubmit}>
                <input type="number" value={fuel.amount} onChange={(e) => setFuel({ ...fuel, amount: e.target.value })} placeholder="Fuel amount (L)" />
                <input type="number" value={fuel.odometer} onChange={(e) => setFuel({ ...fuel, odometer: e.target.value })} placeholder="Current odometer (km)" />
                <input type="date" value={fuel.date} onChange={(e) => setFuel({ ...fuel, date: e.target.value })} />
                <button type="submit" className="btn-primary">Add Fuel</button>
              </form>
            </div>
          </div>
        )}

        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

// Log Complaint Modal
const LogComplaintModal = ({ trip, onClose, onSubmit }) => {
  const [complaint, setComplaint] = useState("");
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!complaint) return;
    await onSubmit(complaint);
    setComplaint("");
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal" ref={modalRef}>
        <h3>⚠️ Log Complaint for Trip: {trip.trip_code}</h3>
        <form className="form-block" onSubmit={handleSubmit}>
          <div className="form-cell">
            <textarea value={complaint} onChange={(e) => setComplaint(e.target.value)} placeholder="Enter complaint details" rows="4" style={{ width: "100%", padding: "8px" }} />
          </div>
          <div className="modal-actions">
            <button type="submit" className="btn-primary">Submit Complaint</button>
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function Trips() {
  const [trips, setTrips] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [breakdowns, setBreakdowns] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [agents, setAgents] = useState([]);
  const [actionTripId, setActionTripId] = useState(null);
  const [viewTripId, setViewTripId] = useState(null);
  const [complaintTripId, setComplaintTripId] = useState(null);
  const [addCostTripId, setAddCostTripId] = useState(null);
  const [updateStatusTripId, setUpdateStatusTripId] = useState(null);
  const [viewStatusTripId, setViewStatusTripId] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [editingTripId, setEditingTripId] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    createTrip: true,
    tripsList: true,
  });
  const modalRef = useRef(null);
  const dropdownRef = useRef(null);
  const [openDropdown, setOpenDropdown] = useState(null);

  const currencies = ["USD", "ZAR", "BWP", "ZMW", "TZS"];

  const [form, setForm] = useState({
    booking_ref: "",
    truck: "",
    driver: "",
    trailers: [],
    breakdowns: [],
    start_km: "",
    origin: "",
    route_id: "",
    destination: "",
    consignment_description: "",
    tonnage: "",
    special_instructions: "",
    initial_fuel_cost: "",
    initial_dispatch_money: "",
    currency_fuel: "USD",
    currency_dispatch: "USD",
    status: "draft",
  });

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      setFeedback(null);
      try {
        await Promise.all([
          loadTrips(),
          loadDrivers(),
          loadTrucks(),
          loadTrailers(),
          loadBreakdowns(),
          loadRoutes(),
          loadAgents(),
        ]);
        console.log("Trips data loaded");
      } catch (error) {
        console.error("Error loading data:", error.code, error.message);
        setFeedback({
          message: `Failed to load data: ${error.message}`,
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setActionTripId(null);
        setViewTripId(null);
        setComplaintTripId(null);
        setAddCostTripId(null);
        setUpdateStatusTripId(null);
        setViewStatusTripId(null);
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  async function loadTrips() {
    const snaps = await getDocs(collection(db, "trips"));
    setTrips(snaps.docs.map((d) => ({ id: d.id, ...d.data() })));
  }

  async function loadDrivers() {
    const snaps = await getDocs(collection(db, "drivers"));
    setDrivers(snaps.docs.map((d) => ({ id: d.id, full_name: d.data().full_name || d.data().name || "Unknown" })));
  }

  async function loadTrucks() {
    const snaps = await getDocs(collection(db, "trucks"));
    setTrucks(snaps.docs.map((t) => ({ id: t.id, reg_no: t.data().reg_no || "Unknown", disposed: t.data().disposed ?? false })));
  }

  async function loadTrailers() {
    const snaps = await getDocs(collection(db, "trailers"));
    setTrailers(snaps.docs.map((t) => ({ id: t.id, trailer_number: t.data().trailer_number || t.data().trailer_code || "Unknown" })));
  }

  async function loadBreakdowns() {
    const snaps = await getDocs(collection(db, "clearances"));
    setBreakdowns(snaps.docs.map((b) => ({ id: b.id, description: b.data().name || b.data().description || "Unknown", cost: b.data().totalCostDefault || 0, currency: "USD" })));
  }

  async function loadRoutes() {
    const snaps = await getDocs(collection(db, "routes"));
    setRoutes(snaps.docs.map((r) => ({ id: r.id, ...r.data() })));
  }

  async function loadAgents() {
    const snaps = await getDocs(collection(db, "agents"));
    setAgents(snaps.docs.map((a) => ({ id: a.id, name: a.data().name || "Unknown", contact: a.data().contact || "N/A" })));
  }

  async function validateTrip() {
    if (!form.booking_ref) {
      setFeedback({ message: "Booking reference is required.", type: "error" });
      return false;
    }
    if (!form.truck) {
      setFeedback({ message: "Please select a truck.", type: "error" });
      return false;
    }
    if (!form.consignment_description) {
      setFeedback({ message: "Consignment description is required.", type: "error" });
      return false;
    }
    if (!form.tonnage) {
      setFeedback({ message: "Tonnage is required.", type: "error" });
      return false;
    }
    if (form.tonnage && Number(form.tonnage) <= 0) {
      setFeedback({ message: "Tonnage must be greater than 0.", type: "error" });
      return false;
    }
    const selectedTruck = trucks.find((t) => t.reg_no === form.truck);
    if (selectedTruck && selectedTruck.disposed) {
      setFeedback({ message: "Cannot assign to a disposed truck.", type: "error" });
      return false;
    }
    if (form.start_km && Number(form.start_km) < 0) {
      setFeedback({ message: "Start odometer cannot be negative.", type: "error" });
      return false;
    }
    if (Number(form.initial_fuel_cost.replace(/,/g, "")) < 0) {
      setFeedback({ message: "Initial fuel cost cannot be negative.", type: "error" });
      return false;
    }
    if (Number(form.initial_dispatch_money.replace(/,/g, "")) < 0) {
      setFeedback({ message: "Initial dispatch money cannot be negative.", type: "error" });
      return false;
    }
    return true;
  }

  async function handleTripSubmit(e) {
    e.preventDefault();
    setFeedback(null);

    if (!(await validateTrip())) return;

    const parseNum = (val) => Number(String(val).replace(/,/g, "")) || 0;

    const initialCost =
      parseNum(form.initial_fuel_cost) +
      parseNum(form.initial_dispatch_money) +
      (form.breakdowns
        ? breakdowns
            .filter((b) => form.breakdowns.includes(b.id))
            .reduce((sum, b) => sum + (b.cost || 0), 0)
        : 0);

    const payload = {
      trip_code: editingTripId ? form.trip_code : "TRP-" + uuidv4().slice(0, 8).toUpperCase(),
      booking_ref: form.booking_ref,
      truck: form.truck,
      driver: form.driver,
      trailers: form.trailers,
      breakdowns: form.breakdowns,
      start_km: Number(form.start_km || 0),
      origin: form.origin,
      route_id: form.route_id,
      destination: form.destination,
      consignment_description: form.consignment_description,
      tonnage: parseNum(form.tonnage),
      special_instructions: form.special_instructions || "",
      initial_fuel_cost: parseNum(form.initial_fuel_cost),
      initial_dispatch_money: parseNum(form.initial_dispatch_money),
      initial_cost: initialCost,
      total_cost: initialCost,
      currency_fuel: form.currency_fuel,
      currency_dispatch: form.currency_dispatch,
      status: form.status,
      createdAt: editingTripId ? form.createdAt : serverTimestamp(),
      extra_costs: editingTripId ? form.extra_costs || [] : [],
      fuel_records: editingTripId ? form.fuel_records || [] : [],
    };

    try {
      let tripId;
      if (editingTripId) {
        await updateDoc(doc(db, "trips", editingTripId), payload);
        tripId = editingTripId;
        setFeedback({ message: "Trip updated successfully.", type: "success" });
      } else {
        const docRef = await addDoc(collection(db, "trips"), payload);
        tripId = docRef.id;
        setFeedback({ message: "Trip created successfully.", type: "success" });
      }
      if (payload.breakdowns?.length > 0) {
        for (const breakdownId of payload.breakdowns) {
          const breakdown = breakdowns.find((b) => b.id === breakdownId);
          if (breakdown) {
            await addDoc(collection(db, "finance"), {
              trip_id: tripId,
              type: "breakdown",
              description: breakdown.description,
              amount: breakdown.cost,
              currency: breakdown.currency,
              date: serverTimestamp(),
            });
          }
        }
      }
      resetForm();
      await loadTrips();
      generateDispatchList({ ...payload, id: tripId });
    } catch (error) {
      console.error("Error saving trip:", error);
      setFeedback({ message: `Failed to save trip: ${error.message}`, type: "error" });
    }
  }

  function confirmDeleteTrip(id) {
    const trip = trips.find((t) => t.id === id);
    if (trip.status !== "draft") {
      setFeedback({ message: "Only draft trips can be deleted.", type: "error" });
      return;
    }
    setConfirmModal({
      title: "Delete Trip",
      message: `Are you sure you want to delete trip "${trip.trip_code}"? This action cannot be undone.`,
      confirmText: "Delete",
      confirmVariant: "danger",
      onConfirm: () => deleteTrip(id),
    });
    setActionTripId(null);
  }

  async function deleteTrip(id) {
    try {
      await deleteDoc(doc(db, "trips", id));
      setFeedback({ message: "Trip deleted successfully.", type: "success" });
      await loadTrips();
    } catch (error) {
      console.error("Error deleting trip:", error);
      setFeedback({ message: `Failed to delete trip: ${error.message}`, type: "error" });
    }
  }

  async function startTrip(id) {
    const trip = trips.find((t) => t.id === id);
    if (!trip.route_id) {
      setFeedback({ message: "Cannot start trip: Route is required.", type: "error" });
      return;
    }
    if (trip.status === "Active" || trip.status === "Completed") {
      setFeedback({ message: `Trip is already ${trip.status.toLowerCase()}.`, type: "error" });
      return;
    }
    try {
      await updateDoc(doc(db, "trips", id), { status: "Active" });
      setFeedback({ message: "Trip started successfully.", type: "success" });
      await loadTrips();
    } catch (error) {
      console.error("Error starting trip:", error);
      setFeedback({ message: `Failed to start trip: ${error.message}`, type: "error" });
    }
  }

  async function addExtraCost(tripId, cost) {
    try {
      const trip = trips.find((t) => t.id === tripId);
      const extraCosts = trip.extra_costs ? [...trip.extra_costs, cost] : [cost];
      const totalExtraCost = extraCosts.reduce((sum, ec) => sum + (ec.amount || 0), 0);
      const breakdownCost = trip.breakdowns ? breakdowns.filter((b) => trip.breakdowns.includes(b.id)).reduce((sum, b) => sum + (b.cost || 0), 0) : 0;
      const newTotalCost = trip.initial_cost + totalExtraCost + breakdownCost;

      await updateDoc(doc(db, "trips", tripId), { extra_costs: extraCosts, total_cost: newTotalCost });
      await addDoc(collection(db, "finance"), { trip_id: tripId, type: "extra_cost", description: cost.description, amount: cost.amount, currency: cost.currency, date: serverTimestamp() });
      setFeedback({ message: "Extra cost added successfully.", type: "success" });
      await loadTrips();
    } catch (error) {
      console.error("Error adding extra cost:", error);
      setFeedback({ message: `Failed to add extra cost: ${error.message}`, type: "error" });
    }
  }

  async function addFuelRecord(tripId, fuel) {
    try {
      const trip = trips.find((t) => t.id === tripId);
      const fuelRecords = trip.fuel_records ? [...trip.fuel_records, fuel] : [fuel];
      await updateDoc(doc(db, "trips", tripId), { fuel_records: fuelRecords });
      await addDoc(collection(db, "finance"), { trip_id: tripId, type: "fuel", description: `Fuel added: ${fuel.amount}L at ${fuel.odometer} km`, amount: 0, currency: trip.currency_dispatch, date: serverTimestamp() });
      setFeedback({ message: "Fuel record added successfully.", type: "success" });
      await loadTrips();
    } catch (error) {
      console.error("Error adding fuel record:", error);
      setFeedback({ message: `Failed to add fuel record: ${error.message}`, type: "error" });
    }
  }

  async function addCostToTrip(tripId, costType, costData) {
    try {
      const trip = trips.find((t) => t.id === tripId);
      if (!trip) return;

      let additionalCost = 0;
      let costDescription = "";

      if (costType === "fuel") {
        const totalCost = parseFloat(costData.liters) * parseFloat(costData.cost_per_liter);
        additionalCost = totalCost;
        costDescription = `Fuel: ${costData.liters}L at ${costData.odometer}km - ${costData.date}`;
        const fuelRecords = trip.fuel_records ? [...trip.fuel_records, { date: costData.date, amount: parseFloat(costData.liters), odometer: parseFloat(costData.odometer), cost: totalCost }] : [{ date: costData.date, amount: parseFloat(costData.liters), odometer: parseFloat(costData.odometer), cost: totalCost }];
        await updateDoc(doc(db, "trips", tripId), { fuel_records: fuelRecords });
      } else if (costType === "mechanical") {
        additionalCost = parseFloat(costData.amount);
        costDescription = `Mechanical (${costData.component}): ${costData.description} - ${costData.date}`;
      } else if (costType === "road") {
        additionalCost = parseFloat(costData.amount);
        costDescription = `Road Cost (${costData.fine_type}): ${costData.description} - ${costData.date}`;
      } else if (costType === "customs") {
        additionalCost = parseFloat(costData.amount);
        costDescription = `Customs (${costData.fine_type}): ${costData.description} - ${costData.date}`;
      }

      const extraCosts = trip.extra_costs ? [...trip.extra_costs, { description: costDescription, amount: additionalCost, currency: trip.currency_dispatch, date: costData.date, type: costType }] : [{ description: costDescription, amount: additionalCost, currency: trip.currency_dispatch, date: costData.date, type: costType }];
      const totalExtraCost = extraCosts.reduce((sum, ec) => sum + (ec.amount || 0), 0);
      const breakdownCost = trip.breakdowns ? breakdowns.filter((b) => trip.breakdowns.includes(b.id)).reduce((sum, b) => sum + (b.cost || 0), 0) : 0;
      const newTotalCost = trip.initial_cost + totalExtraCost + breakdownCost;

      await updateDoc(doc(db, "trips", tripId), { extra_costs: extraCosts, total_cost: newTotalCost });
      await addDoc(collection(db, "finance"), { trip_id: tripId, type: "extra_cost", description: costDescription, amount: additionalCost, currency: trip.currency_dispatch, date: serverTimestamp() });
      setFeedback({ message: `${costType.charAt(0).toUpperCase() + costType.slice(1)} cost added successfully.`, type: "success" });
      await loadTrips();
    } catch (error) {
      console.error("Error adding cost:", error);
      setFeedback({ message: `Failed to add cost: ${error.message}`, type: "error" });
    }
  }

  async function updateTripStatus(tripId, action, statusData) {
    try {
      const trip = trips.find((t) => t.id === tripId);
      if (!trip) return;

      let updatePayload = {};

      if (action === "complete") {
        updatePayload = { status: "Completed", final_odometer: parseFloat(statusData.final_odometer), pods_submitted: statusData.pods_submitted, completed_date: serverTimestamp() };
        setFeedback({ message: "Trip marked as completed successfully.", type: "success" });
      } else if (action === "delayed") {
        updatePayload = { status: "Delayed", delay_reason: statusData.delay_reason, delay_description: statusData.delay_description, delayed_date: serverTimestamp() };
        setFeedback({ message: "Trip marked as delayed.", type: "success" });
      } else if (action === "not_completed") {
        updatePayload = { status: "Not Completed", not_completed_reason: statusData.not_completed_reason, not_completed_notes: statusData.not_completed_notes, not_completed_date: serverTimestamp() };
        setFeedback({ message: "Trip marked as not completed.", type: "success" });
      }

      await updateDoc(doc(db, "trips", tripId), updatePayload);
      await loadTrips();
    } catch (error) {
      console.error("Error updating trip status:", error);
      setFeedback({ message: `Failed to update status: ${error.message}`, type: "error" });
    }
  }

  async function logComplaint(tripId, description) {
    try {
      await addDoc(collection(db, "complaints"), { trip_id: tripId, description, createdAt: serverTimestamp() });
      setFeedback({ message: "Complaint logged successfully.", type: "success" });
    } catch (error) {
      console.error("Error logging complaint:", error);
      setFeedback({ message: `Failed to log complaint: ${error.message}`, type: "error" });
    }
  }

  function editTrip(trip) {
    if (trip.status !== "draft") {
      setFeedback({ message: "Only draft trips can be edited.", type: "error" });
      return;
    }
    setForm({
      booking_ref: trip.booking_ref || "",
      truck: trip.truck || "",
      driver: trip.driver || "",
      trailers: trip.trailers || [],
      breakdowns: trip.breakdowns || [],
      start_km: trip.start_km ? String(trip.start_km) : "",
      origin: trip.origin || "",
      route_id: trip.route_id || "",
      destination: trip.destination || "",
      consignment_description: trip.consignment_description || "",
      tonnage: trip.tonnage ? String(trip.tonnage) : "",
      special_instructions: trip.special_instructions || "",
      initial_fuel_cost: trip.initial_fuel_cost ? String(trip.initial_fuel_cost) : "",
      initial_dispatch_money: trip.initial_dispatch_money ? String(trip.initial_dispatch_money) : "",
      currency_fuel: trip.currency_fuel || "USD",
      currency_dispatch: trip.currency_dispatch || "USD",
      status: trip.status || "draft",
      trip_code: trip.trip_code || "",
      createdAt: trip.createdAt || serverTimestamp(),
      extra_costs: trip.extra_costs || [],
      fuel_records: trip.fuel_records || [],
    });
    setEditingTripId(trip.id);
    setActionTripId(null);
  }

  function resetForm() {
    setForm({
      booking_ref: "",
      truck: "",
      driver: "",
      trailers: [],
      breakdowns: [],
      start_km: "",
      origin: "",
      route_id: "",
      destination: "",
      consignment_description: "",
      tonnage: "",
      special_instructions: "",
      initial_fuel_cost: "",
      initial_dispatch_money: "",
      currency_fuel: "USD",
      currency_dispatch: "USD",
      status: "draft",
    });
    setEditingTripId(null);
    setActionTripId(null);
  }

  function generateDispatchList(trip) {
    const route = routes.find((r) => r.id === trip.route_id);
    const routeName = route ? route.name : "Unknown";
    const routePoints = route ? route.points.join(" → ") : "N/A";
    const selectedBreakdowns = breakdowns.filter((b) => trip.breakdowns?.includes(b.id));
    const totalBreakdownCost = selectedBreakdowns.reduce((sum, b) => sum + (b.cost || 0), 0);

    const dispatchContent = `
╔══════════════════════════════════════════════════════════════════╗
║                    DISPATCH LIST - ${trip.trip_code}                    ║
╚══════════════════════════════════════════════════════════════════╝

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  TRIP DETAILS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  Trip Code:              ${trip.trip_code}
  Booking Reference:      ${trip.booking_ref}
  Status:                 ${trip.status}
  Created At:             ${new Date(trip.createdAt).toLocaleDateString()}

  ──────────────────────────────────────────────────────────────────

  Truck:                  ${trip.truck || "N/A"}
  Driver:                 ${trip.driver || "N/A"}
  Trailers:               ${trip.trailers?.length > 0 ? trip.trailers.join(", ") : "None"}

  ──────────────────────────────────────────────────────────────────

  Start Odometer:         ${trip.start_km || "N/A"} km
  Origin:                 ${trip.origin || "N/A"}
  Route:                  ${routeName}
                          ${routePoints}
  Destination:            ${trip.destination || "N/A"}

  ──────────────────────────────────────────────────────────────────

  Consignment:            ${trip.consignment_description || "N/A"}
  Tonnage:                ${trip.tonnage || "0"} tons
  Special Instructions:   ${trip.special_instructions || "None"}

  Agents:                 ${trip.agents?.join(", ") || "None"}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  FINANCIAL DETAILS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  Initial Fuel Cost:      ${trip.currency_fuel} ${trip.initial_fuel_cost?.toLocaleString() || 0}
  Initial Dispatch Money: ${trip.currency_dispatch} ${trip.initial_dispatch_money?.toLocaleString() || 0}

  ──────────────────────────────────────────────────────────────────

  Cost Templates:         ${selectedBreakdowns.length > 0 ? selectedBreakdowns.map(b => `\n                          ${b.description}: ${b.currency} ${b.cost.toLocaleString()}`).join("") : "None"}
  Total Templates Cost:   ${trip.currency_dispatch} ${totalBreakdownCost.toLocaleString()}

  ──────────────────────────────────────────────────────────────────

  Total Initial Cost:     ${trip.currency_dispatch} ${trip.initial_cost.toLocaleString()}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`;

    const blob = new Blob([dispatchContent], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Dispatch_List_${trip.trip_code}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    setFeedback({ message: "Dispatch list generated successfully.", type: "success" });
  }

  function handleNumericChange(field, value) {
    const clean = value.replace(/[^0-9.,]/g, "").replace(/,/g, "");
    if (clean === "") return setForm({ ...form, [field]: "" });
    const parts = clean.split(".");
    let formatted = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",") + (parts[1] ? "." + parts[1].slice(0, 2) : "");
    setForm({ ...form, [field]: formatted });
  }

  const toggleMultiSelect = (field, option) => {
    const current = form[field];
    if (current.includes(option)) {
      setForm({ ...form, [field]: current.filter((o) => o !== option) });
    } else {
      setForm({ ...form, [field]: [...current, option] });
    }
  };

  const renderDropdown = (label, field, options, multi = false) => {
    const isPlaceholder = !form[field] || (Array.isArray(form[field]) && form[field].length === 0);
    return (
      <div className="dropdown-wrapper" ref={dropdownRef}>
        <div
          className={`select-control dropdown-input ${openDropdown === field ? "active" : ""} ${
            isPlaceholder ? "placeholder" : ""
          }`}
          onClick={() => setOpenDropdown(openDropdown === field ? null : field)}
        >
          {multi && form[field].length > 0 ? (
            <div className="selected-tags">
              {form[field].map((item) => (
                <span
                  key={item}
                  className="tag"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleMultiSelect(field, item);
                  }}
                >
                  {field === "breakdowns"
                    ? breakdowns.find((b) => b.id === item)?.description || item
                    : item} ✕
                </span>
              ))}
            </div>
          ) : isPlaceholder ? (
            `Select ${label}`
          ) : field === "route_id" ? (
            routes.find((r) => r.id === form[field])?.name || form[field]
          ) : field === "status" ? (
            `Trip Status: ${form[field]}`
          ) : (
            form[field]
          )}
        </div>
        {openDropdown === field && (
          <div className="dropdown">
            {options.length > 0 ? (
              options.map((opt) => (
                <div
                  key={multi ? opt.id || opt : field === "route_id" ? opt.id : opt}
                  className={`dropdown-option ${
                    (multi
                      ? form[field].includes(opt.id || opt)
                      : form[field] === (field === "route_id" ? opt.id : opt))
                      ? "selected"
                      : ""
                  } ${
                    field === "truck" && trucks.find((t) => t.reg_no === opt)?.disposed
                      ? "disabled"
                      : field === "status" && opt === "Completed"
                      ? "disabled"
                      : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (field === "truck" && trucks.find((t) => t.reg_no === opt)?.disposed) return;
                    if (field === "status" && opt === "Completed") return;
                    multi
                      ? toggleMultiSelect(field, opt.id || opt)
                      : setForm({ ...form, [field]: field === "route_id" ? opt.id : opt });
                    if (!multi) setOpenDropdown(null);
                  }}
                >
                  {field === "route_id" ? opt.name : field === "status" ? `Trip Status: ${opt}` : opt}
                  {field === "truck" && trucks.find((t) => t.reg_no === opt)?.disposed ? " (Disposed)" : ""}
                </div>
              ))
            ) : (
              <div className="dropdown-option">No options available</div>
            )}
          </div>
        )}
      </div>
    );
  };

  const handleSectionToggle = (sectionId) => {
    setExpandedSections({
      ...expandedSections,
      [sectionId]: !expandedSections[sectionId],
    });
  };

  return (
    <div className="trips-page">
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {feedback && (
            <FeedbackModal
              message={feedback.message}
              type={feedback.type}
              onClose={() => setFeedback(null)}
            />
          )}
          <h2>✈️ Trips</h2>
          <CollapsibleSection
            title={editingTripId ? "✏️ Edit Trip" : "➕ Create Trip"}
            id="createTrip"
            isExpanded={expandedSections.createTrip}
            onToggle={handleSectionToggle}
            externalControl={true}
          >
            <form className="trips-form-create" onSubmit={handleTripSubmit}>
              {/* Row 1: Booking Ref, Truck, Driver, Trailers */}
              <div className="form-grid">
                <div className="form-cell">
                  <input
                    value={form.booking_ref}
                    onChange={(e) => setForm({ ...form, booking_ref: e.target.value })}
                    placeholder="📋 Booking Reference"
                    required
                  />
                </div>
                <div className="form-cell">
                  {renderDropdown("🚛 Truck", "truck", trucks.map((t) => t.reg_no))}
                </div>
                <div className="form-cell">
                  {renderDropdown("👤 Driver", "driver", drivers.map((d) => d.full_name))}
                </div>
                <div className="form-cell">
                  {renderDropdown("📦 Trailers", "trailers", trailers.map((t) => t.trailer_number), true)}
                </div>
              </div>

              {/* Row 2: Start Odometer, Origin, Route, Destination */}
              <div className="form-grid">
                <div className="form-cell">
                  <input
                    type="number"
                    value={form.start_km}
                    onChange={(e) => setForm({ ...form, start_km: e.target.value })}
                    placeholder="📊 Start Odometer (km)"
                  />
                </div>
                <div className="form-cell">
                  <input
                    value={form.origin}
                    onChange={(e) => setForm({ ...form, origin: e.target.value })}
                    placeholder="📍 Origin"
                  />
                </div>
                <div className="form-cell">
                  {renderDropdown("🛣️ Route", "route_id", routes)}
                </div>
                <div className="form-cell">
                  <input
                    value={form.destination}
                    onChange={(e) => setForm({ ...form, destination: e.target.value })}
                    placeholder="🏁 Destination"
                  />
                </div>
              </div>

              {/* Row 3: Consignment Description, Tonnage, Special Instructions, Initial Fuel Cost */}
              <div className="form-grid">
                <div className="form-cell">
                  <input
                    value={form.consignment_description}
                    onChange={(e) => setForm({ ...form, consignment_description: e.target.value })}
                    placeholder="📦 Consignment Description"
                    required
                  />
                </div>
                <div className="form-cell">
                  <div className="tonnage-wrapper">
                    <input
                      type="number"
                      step="0.1"
                      value={form.tonnage}
                      onChange={(e) => setForm({ ...form, tonnage: e.target.value })}
                      placeholder="⚖️ Tonnage (tons)"
                      required
                    />
                    <span className="tonnage-unit">tons</span>
                  </div>
                </div>
                <div className="form-cell">
                  <input
                    value={form.special_instructions}
                    onChange={(e) => setForm({ ...form, special_instructions: e.target.value })}
                    placeholder="📝 Special Instructions"
                  />
                </div>
                <div className="form-cell">
                  <div className="currency-input-wrapper">
                    <input
                      type="text"
                      value={form.initial_fuel_cost}
                      onChange={(e) => handleNumericChange("initial_fuel_cost", e.target.value)}
                      placeholder="⛽ Initial Fuel Cost"
                      className="currency-input"
                    />
                    <select
                      value={form.currency_fuel}
                      onChange={(e) => setForm({ ...form, currency_fuel: e.target.value })}
                      className="currency-select"
                    >
                      {currencies.map((c) => (<option key={c}>{c}</option>))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Row 4: Initial Dispatch Money, Cost Templates, Trip Status */}
              <div className="form-grid">
                <div className="form-cell">
                  <div className="currency-input-wrapper">
                    <input
                      type="text"
                      value={form.initial_dispatch_money}
                      onChange={(e) => handleNumericChange("initial_dispatch_money", e.target.value)}
                      placeholder="💰 Initial Dispatch Money"
                      className="currency-input"
                    />
                    <select
                      value={form.currency_dispatch}
                      onChange={(e) => setForm({ ...form, currency_dispatch: e.target.value })}
                      className="currency-select"
                    >
                      {currencies.map((c) => (<option key={c}>{c}</option>))}
                    </select>
                  </div>
                </div>
                <div className="form-cell">
                  {renderDropdown("📋 Cost Templates", "breakdowns", breakdowns, true)}
                </div>
                <div className="form-cell">
                  {renderDropdown("🎯 Trip Status", "status", ["draft", "Active"])}
                </div>
              </div>

              <div className="form-actions create-trip-actions">
                <button type="submit" className="btn-primary">
                  {editingTripId ? "✏️ Update Trip" : "➕ Create Trip"}
                </button>
                {editingTripId && (
                  <button type="button" className="btn-secondary" onClick={resetForm}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </CollapsibleSection>

          <CollapsibleSection
            title="📋 Trips List"
            id="tripsList"
            isExpanded={expandedSections.tripsList}
            onToggle={handleSectionToggle}
            externalControl={true}
          >
            <div className="table-wrap">
              <table className="simple-table">
                <thead>
                  <tr>
                    <th>Trip Code</th>
                    <th>Driver</th>
                    <th>Truck</th>
                    <th>Route</th>
                    <th>Destination</th>
                    <th>Consignment</th>
                    <th>Tonnage</th>
                    <th>Initial Cost</th>
                    <th>Total Cost</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {trips.length === 0 ? (
                    <tr>
                      <td colSpan="12">No trips found</td>
                    </tr>
                  ) : (
                    trips.map((t) => (
                      <tr key={t.id}>
                        <td data-label="Trip Code">{t.trip_code}</td>
                        <td data-label="Driver">{t.driver || "-"}</td>
                        <td data-label="Truck">{t.truck || "-"}</td>
                        <td data-label="Route">{routes.find((r) => r.id === t.route_id)?.name || "-"}</td>
                        <td data-label="Destination">{t.destination || "-"}</td>
                        <td data-label="Consignment">{t.consignment_description?.substring(0, 30) || "-"}</td>
                        <td data-label="Tonnage">{t.tonnage || "0"} tons</td>
                        <td data-label="Initial Cost">{t.currency_dispatch} {t.initial_cost?.toLocaleString() || 0}</td>
                        <td data-label="Total Cost">{t.currency_dispatch} {t.total_cost?.toLocaleString() || 0}</td>
                        <td data-label="Status">
                          <span className={`status-badge status-${t.status?.toLowerCase()}`} onClick={() => setViewStatusTripId(t.id)} style={{ cursor: "pointer" }}>
                            {t.status || "draft"}
                          </span>
                        </td>
                        <td data-label="Date">{t.createdAt?.slice(0, 10) || "-"}</td>
                        <td data-label="Actions">
                          <button className="menu-button" onClick={() => setActionTripId(t.id)}>⋮</button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CollapsibleSection>

          {actionTripId && (
            <ActionModal
              trip={trips.find((t) => t.id === actionTripId)}
              onClose={() => setActionTripId(null)}
              onGenerateDispatch={() => generateDispatchList(trips.find((t) => t.id === actionTripId))}
              onStart={() => startTrip(actionTripId)}
              onEdit={() => editTrip(trips.find((t) => t.id === actionTripId))}
              onDelete={() => confirmDeleteTrip(actionTripId)}
              onLogComplaint={() => setComplaintTripId(actionTripId)}
              onViewTrip={() => { setViewTripId(actionTripId); setActionTripId(null); }}
              onAddCost={() => { setAddCostTripId(actionTripId); setActionTripId(null); }}
              onUpdateStatus={() => { setUpdateStatusTripId(actionTripId); setActionTripId(null); }}
            />
          )}

          {addCostTripId && <AddCostModal trip={trips.find((t) => t.id === addCostTripId)} onClose={() => setAddCostTripId(null)} onAddCost={(type, data) => addCostToTrip(addCostTripId, type, data)} />}
          {updateStatusTripId && <UpdateStatusModal trip={trips.find((t) => t.id === updateStatusTripId)} onClose={() => setUpdateStatusTripId(null)} onUpdateStatus={(action, data) => updateTripStatus(updateStatusTripId, action, data)} />}
          {viewStatusTripId && <ViewStatusDetailsModal trip={trips.find((t) => t.id === viewStatusTripId)} onClose={() => setViewStatusTripId(null)} />}
          {viewTripId && <ViewTripModal trip={trips.find((t) => t.id === viewTripId)} breakdowns={breakdowns} routes={routes} agents={agents} onClose={() => setViewTripId(null)} onAddExtraCost={(cost) => addExtraCost(viewTripId, cost)} onAddFuel={(fuel) => addFuelRecord(viewTripId, fuel)} />}
          {complaintTripId && <LogComplaintModal trip={trips.find((t) => t.id === complaintTripId)} onClose={() => setComplaintTripId(null)} onSubmit={(description) => logComplaint(complaintTripId, description)} />}
          {confirmModal && <ConfirmModal title={confirmModal.title} message={confirmModal.message} confirmText={confirmModal.confirmText} confirmVariant={confirmModal.confirmVariant} onConfirm={confirmModal.onConfirm} onClose={() => setConfirmModal(null)} />}
        </>
      )}
    </div>
  );
}